/**
 * La griglia a due assi, disegnata.
 *
 * Il disegno è quello del prototipo v3 (03-SCHERMATE-TV §5), con due misure cambiate dopo averlo
 * visto su un catalogo vero: la colonna del canale è larga 340 invece di 260 e la riga del tempo va
 * a 5,6 px al minuto invece di 6,6. Il prototipo aveva nomi corti; un fornitore no — e il nome del
 * canale è l'informazione per cui si apre la guida. Righe alte
 * 88 px con 10 px di stacco, programma in onda con lo sfondo più chiaro e la barra di
 * avanzamento, programma cominciato prima della finestra tagliato con un `‹` davanti al
 * titolo.
 *
 * **Quello che rende questa griglia diversa da una lista che poi si ottimizza** sta in
 * tre punti, tutti visibili qui sotto:
 *
 *  1. le righe montate sono solo quelle visibili più il margine — dodicimila righe nel
 *     DOM non sono lente, sono impossibili;
 *  2. il riquadro di selezione è **un solo elemento** che si sposta scrivendo un
 *     `transform`: una freccia non fa ri-rendere React;
 *  3. lo scorrimento è un `transform`, non una proprietà che rifà la disposizione.
 *
 * La struttura serve al punto 2: le celle dei programmi e il riquadro stanno **nello
 * stesso strato**, quello che scorre in orizzontale, così le due coordinate si scrivono
 * nello stesso sistema e il riquadro non può sfasarsi dalle celle.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  PROGRAM_GAP_PX,
  ROW_HEIGHT_PX,
  ROW_PITCH_PX,
  cleanChannelName,
  hasReminder,

  layoutProgram,
  programLiveness,
  programProgressPercent,
  timeSlots,
  timeToPx,
  windowWidthPx,
  type GuideWindow,
  type ProgramStore,
} from '@player/domain';
import { FONT_STACK, colors, layout } from '../theme.js';
import { ChannelLogo } from '../components/ChannelLogo.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { GuideController, GuideCursor } from './GuideController.js';

const TIMELINE_LEFT_PX = layout.guideChannelWidth + 8;

export interface GuideGridProps {
  readonly controller: GuideController;
  readonly programs: ProgramStore;
  readonly window: GuideWindow;
  readonly serverNowMs: number;
  readonly onCursorChange?: (cursor: GuideCursor) => void;
  /**
   * Gli identificativi dei preferiti.
   *
   * Un `Set` e non un elenco: questa riga si disegna per ogni canale visibile a ogni scorrimento, e
   * una ricerca lineare dentro un elenco di preferiti, per riga, per fotogramma, è il genere di
   * costo che non si nota su cinque canali e si sente su dodicimila.
   */
  readonly favoriteIds?: ReadonlySet<string>;
  /**
   * Un clic su una cella o su un canale.
   *
   * Il mouse **non funzionava affatto**: le celle erano `div` senza un solo gestore, mentre nel
   * prototipo sono `button`. Non era una scelta — era una cosa che nessuno aveva scritto, e che non
   * si nota finché si prova solo col telecomando.
   *
   * Un clic solo **sposta** il cursore; il secondo sulla stessa cella sceglie. È la stessa grammatica
   * delle frecce più OK, e soprattutto evita che un clic di passaggio faccia partire un canale.
   */
  readonly onPick?: (channelIndex: number, slot: number, confirmed: boolean) => void;
  /**
   * Inverte il preferito di un canale, dalla stella sulla sua riga.
   *
   * Prima la colonna spiegava «OK sul logo per aggiungere o togliere»: un gesto invisibile con
   * accanto la sua didascalia. Un gesto che va spiegato è un gesto mal disegnato — la correzione è
   * un tasto che si vede, non una frase che lo racconta.
   */
  readonly onToggleFavorite?: (channelId: string) => void;
  /**
   * I promemoria già messi, con la chiave di `reminderKey` — canale più istante, non l'id dell'EPG.
   *
   * Vedi `reminders.ts`: l'identificativo che `materialize` restituisce è sintetizzato dalla
   * posizione, quindi non poteva coincidere con niente di archiviato.
   */
  readonly reminderIds?: ReadonlySet<string>;
  /**
   * Il fuoco è sulla **card del canale** invece che su una cella.
   *
   * Nel disegno sono due bersagli distinti sulla stessa riga: il tasto del canale, dove OK aggiunge o
   * toglie dai preferiti, e le celle dei programmi. Senza questo, la card era premibile solo col
   * mouse — e su un televisore un elemento che si vede e che il telecomando non raggiunge è il
   * difetto peggiore che esista.
   */
  readonly channelFocused?: boolean;
  /**
   * Porta ai preferiti dalla categoria vuota.
   *
   * Nel disegno, «Nessun canale in questa categoria» ha sotto un tasto che ci porta a sceglierli:
   * senza, il messaggio è un vicolo cieco — dice cosa non c'è e non cosa fare.
   */
  readonly onOpenFavorites?: () => void;
}

const EMPTY_FAVORITES: ReadonlySet<string> = new Set();

export function GuideGrid({
  controller,
  programs,
  window: guideWindow,
  serverNowMs,
  onCursorChange,
  favoriteIds = EMPTY_FAVORITES,
  onPick,
  onToggleFavorite,
  reminderIds = EMPTY_FAVORITES,
  channelFocused = false,
  onOpenFavorites,
}: GuideGridProps): ReactNode {
  const translator = useTranslator();
  const [geometry, setGeometry] = useState(() => controller.geometry);
  const verticalRef = useRef<HTMLDivElement | null>(null);
  const horizontalRef = useRef<HTMLDivElement | null>(null);
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const channelHighlightRef = useRef<HTMLDivElement | null>(null);

  // L'unico abbonamento che ri-rende: scatta quando cambia l'insieme delle righe o lo
  // scorrimento, non a ogni freccia.
  useEffect(() => controller.onWindowChange(setGeometry), [controller]);

  /**
   * Il fuoco, senza React.
   *
   * Legge la posizione dal dominio e scrive due proprietà su **un** elemento. È il
   * percorso che il banco di misura cronometra, ed è volutamente corto: nessuna lettura
   * dal DOM — che forzerebbe un ricalcolo della disposizione, la prima delle tre cose che
   * rovinano il fuoco (07-PRESTAZIONI §6) — e nessuna allocazione oltre le due stringhe.
   */
  /** Se il fuoco è sulla card del canale. In un riferimento perché lo legge `paint`, che non ri-rende. */
  const focusedRef = useRef(channelFocused);
  focusedRef.current = channelFocused;

  const paint = useRef<(cursor: GuideCursor) => void>(() => undefined);
  paint.current = (cursor: GuideCursor): void => {
    const highlight = highlightRef.current;
    if (!highlight) return;

    /*
      **Senza programmi il riquadro si vede lo stesso.**

      Prima si nascondeva: `opacity = '0'` e via. Sembra ragionevole — non c'è una cella da
      evidenziare — e rende la guida **inutilizzabile** su un catalogo senza EPG. Il cursore si
      muoveva davvero a ogni freccia, ma sullo schermo non cambiava niente: da fuori è
      «i canali non sono navigabili». Su stage sono 60 canali su 60 senza mappatura, cioè è il caso
      normale e non quello raro.

      Quando non c'è una cella, il riquadro prende **tutta la riga**: dice dove sei, che è l'unica
      cosa che serve quando non c'è altro da dire.
    */
    // Con il fuoco sulla card del canale, la cella non è più il bersaglio: il contorno è là, e qui
    // resta il solo fondo che dice su quale riga si è.
    highlight.style.opacity = focusedRef.current ? '0.001' : '1';
    const topPx = cursor.channelIndex * ROW_PITCH_PX;

    /*
      **Anche la colonna del canale si accende.**

      Nel prototipo il fuoco su una riga dà al tasto del canale `background:#1C2330`; qui il riquadro
      viveva solo nello strato dei programmi, quindi la card del canale restava spenta. Su un canale
      **senza EPG** il risultato era che l'unica cosa evidenziata era un rettangolo vuoto a destra: si
      vedeva la selezione, non su quale canale fosse.

      Due elementi e non uno perché i due strati scorrono in modo diverso — quello dei programmi anche
      in orizzontale — e un riquadro solo si sfaserebbe dalla colonna al primo scorrimento laterale.
    */
    const channelHighlight = channelHighlightRef.current;
    if (channelHighlight) {
      channelHighlight.style.opacity = '1';
      channelHighlight.style.transform = `translate3d(0px, ${topPx}px, 0)`;
    }

    if (programs.programCountOf(cursor.channelIndex) === 0) {
      highlight.style.width = `${windowWidthPx(guideWindow)}px`;
      highlight.style.transform = `translate3d(0px, ${topPx}px, 0)`;
      return;
    }

    const program = programs.materialize(cursor.channelIndex, cursor.slot);
    const placement = layoutProgram(program, guideWindow);
    highlight.style.width = `${placement.widthPx}px`;
    highlight.style.transform = `translate3d(${placement.leftPx}px, ${topPx}px, 0)`;
  };

  useEffect(() => {
    paint.current(controller.currentCursor);
  }, [channelFocused, controller]);

  useEffect(() => {
    const unsubscribe = controller.onCursorChange((cursor) => {
      paint.current(cursor);
      onCursorChange?.(cursor);
    });
    paint.current(controller.currentCursor);
    return unsubscribe;
  }, [controller, onCursorChange]);

  // Lo scorrimento si scrive prima che il browser disegni: con `useEffect` ci sarebbe una
  // cornice in cui le righe sono nuove e lo scorrimento è ancora vecchio.
  useLayoutEffect(() => {
    if (verticalRef.current) {
      verticalRef.current.style.transform = `translate3d(0, ${-geometry.scrollYPx}px, 0)`;
    }
    const horizontal = `translate3d(${-geometry.scrollXPx}px, 0, 0)`;
    if (horizontalRef.current) horizontalRef.current.style.transform = horizontal;
    if (rulerRef.current) rulerRef.current.style.transform = horizontal;
    paint.current(controller.currentCursor);
  }, [geometry, controller]);

  const { range } = geometry;
  const timelineWidthPx = windowWidthPx(guideWindow);
  const totalHeightPx = controller.channelCount * ROW_PITCH_PX;

  const channelCells: ReactNode[] = [];
  const programCells: ReactNode[] = [];
  for (let index = range.firstIndex; index < range.endIndex; index += 1) {
    const channel = controller.channelAt(index);
    if (!channel) continue;
    channelCells.push(
      <ChannelCell
        key={channel.id}
        topPx={index * ROW_PITCH_PX}
        channelIndex={index}
        number={channel.number}
        channelId={channel.id}
        rawName={channel.name}
        logoUrl={channel.logoUrl}
        favorite={favoriteIds.has(channel.id)}
        onPick={onPick}
        onToggleFavorite={onToggleFavorite === undefined ? undefined : () => onToggleFavorite(channel.id)}
      />,
    );
    programCells.push(
      <ProgramCells
        key={channel.id}
        channelIndex={index}
        topPx={index * ROW_PITCH_PX}
        programs={programs}
        window={guideWindow}
        serverNowMs={serverNowMs}
        reminderIds={reminderIds}
        onPick={onPick}
      />,
    );
  }

  if (controller.channelCount === 0) {
    return (
      <div
        data-guide-empty="1"
        style={{ padding: '60px 0', display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'flex-start' }}
      >
        <span style={{ font: `600 30px ${FONT_STACK}` }}>{translator.t('guide.empty')}</span>
        {onOpenFavorites !== undefined && (
          <button
            type="button"
            data-f="1"
            data-guide-choose-favorites="1"
            onClick={onOpenFavorites}
            style={{
              height: 68,
              padding: '0 30px',
              border: 0,
              borderRadius: 14,
              background: colors.surfaceButton,
              color: colors.text,
              font: `600 25px ${FONT_STACK}`,
              cursor: 'pointer',
              outline: `4px solid ${colors.accent}`,
              outlineOffset: 4,
            }}
          >
            {translator.t('guide.chooseFavorites')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div
        style={{
          position: 'relative',
          flex: 'none',
          height: layout.guideRulerHeight,
          marginLeft: TIMELINE_LEFT_PX,
          borderBottom: `1px solid ${colors.borderFaint}`,
          overflow: 'hidden',
        }}
      >
        <div ref={rulerRef} style={{ position: 'absolute', inset: 0, willChange: 'transform' }}>
          {timeSlots(guideWindow).map((slot) => (
            <div
              key={slot.atMs}
              style={{
                position: 'absolute',
                top: 6,
                left: slot.leftPx,
                font: `600 21px ${FONT_STACK}`,
                color: colors.textDim,
              }}
            >
              {translator.time(slot.atMs)}
            </div>
          ))}
          {serverNowMs >= guideWindow.startMs && serverNowMs < guideWindow.endMs ? (
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: timeToPx(serverNowMs, guideWindow),
                width: 3,
                height: 18,
                background: colors.liveBright,
              }}
            />
          ) : null}
        </div>
      </div>

      <div style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div
          ref={verticalRef}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, height: totalHeightPx, willChange: 'transform' }}
        >
          <div style={{ position: 'absolute', top: 0, left: 0, width: layout.guideChannelWidth }}>
            <div
              ref={channelHighlightRef}
              data-guide-channel-highlight="1"
              aria-hidden
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: layout.guideChannelWidth,
                height: ROW_HEIGHT_PX,
                borderRadius: 12,
                background: colors.surfaceRaised,
                opacity: 0,
                pointerEvents: 'none',
                willChange: 'transform',
                // Il contorno solo quando il fuoco è **qui**: è il segno che dice cosa preme OK.
                outline: channelFocused ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 2,
              }}
            />
            {channelCells}
          </div>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: TIMELINE_LEFT_PX,
              right: 0,
              height: totalHeightPx,
              overflow: 'hidden',
            }}
          >
            <div
              ref={horizontalRef}
              style={{ position: 'absolute', top: 0, left: 0, width: timelineWidthPx, height: totalHeightPx, willChange: 'transform' }}
            >
              {programCells}
              {/*
                La linea rossa del presente, che attraversa **tutte** le righe.

                Nella riga del tempo c'era già una tacca alta 18 px, e non basta: serve a dire che ora
                è, non *dove* siamo nella griglia. Il prototipo tira una linea verticale per tutta
                l'altezza, ed è quella che fa vedere a colpo d'occhio quali programmi sono cominciati.
                Sta in questo strato — quello che scorre in orizzontale — così la sua ascissa è nello
                stesso sistema delle celle e non può sfasarsi da esse.

                `pointerEvents: 'none'` perché è un segno, non un bersaglio: senza, intercetterebbe i
                clic del programma che le sta sotto.
              */}
              {serverNowMs >= guideWindow.startMs && serverNowMs < guideWindow.endMs ? (
                <div
                  data-now-line="1"
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: 0,
                    height: totalHeightPx,
                    left: timeToPx(serverNowMs, guideWindow),
                    width: 3,
                    background: colors.liveBright,
                    opacity: 0.85,
                    pointerEvents: 'none',
                    zIndex: 3,
                  }}
                />
              ) : null}
              <div
                ref={highlightRef}
                data-guide-highlight="1"
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: ROW_HEIGHT_PX,
                  borderRadius: 10,
                  outline: `4px solid ${colors.accent}`,
                  outlineOffset: 2,
                  background: colors.focusProgram,
                  opacity: 0,
                  pointerEvents: 'none',
                  willChange: 'transform',
                  /*
                    **Dietro le celle, non sopra.**

                    Il fondo è opaco (`#33405A`), e disegnato sopra copriva titolo e orario del
                    programma a fuoco: sullo schermo restava un rettangolo azzurro vuoto con il
                    contorno ambra, cioè l'unica cella di cui non si poteva leggere niente era
                    proprio quella che si stava per premere.

                    Dietro funziona per entrambi i casi: su una riga **con** programmi la cella
                    opaca copre il fondo e resta il contorno, che è il segno di fuoco; su una riga
                    **senza** programmi non c'è niente a coprirlo e si vede il riquadro pieno — che
                    è l'unico modo di dire «sei qui» dove non c'è una cella.
                  */
                  zIndex: 0,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChannelCell({
  topPx,
  channelIndex,
  channelId,
  number,
  rawName,
  logoUrl,
  favorite,
  onPick,
  onToggleFavorite,
}: {
  topPx: number;
  channelIndex: number;
  /** Il seme del colore del riquadro: l'identificativo, che non cambia con gli import. */
  channelId: string;
  number: number;
  rawName: string;
  logoUrl: string | null;
  /** Se è fra i preferiti: una stella accanto al numero (03-SCHERMATE-TV, §9). */
  favorite: boolean;
  onPick?: (channelIndex: number, slot: number, confirmed: boolean) => void;
  onToggleFavorite?: () => void;
}): ReactNode {
  const translator = useTranslator();
  const cleaned = cleanChannelName(rawName);
  const displayName = cleaned.usable
    ? cleaned.text
    : translator.t('guide.unnamedChannel', { number });
  return (
    <div
      /*
        Un contenitore con **due** tasti dentro, non un tasto solo.

        La stella deve essere premibile per conto suo, e un `<button>` dentro un `<button>` non è
        HTML valido: i browser lo riparano scollegandoli, e il risultato è un tasto che a volte
        risponde e a volte no — il genere di difetto che si attribuisce al televisore.
      */
      style={{
        position: 'absolute',
        top: topPx,
        left: 0,
        width: layout.guideChannelWidth,
        height: ROW_HEIGHT_PX,
        display: 'flex',
        alignItems: 'center',
        contain: 'layout paint',
        // Sopra il riquadro del fuoco, che è un fondo e non deve coprire il logo né il nome.
        zIndex: 1,
      }}
    >
    <button
      type="button"
      data-f="1"
      data-channel-row={channelIndex}
      /*
        Un **tasto**, non un riquadro.

        Nel prototipo la colonna del canale è `<button data-f="1">`. Qui era un `div`: col mouse non
        succedeva niente, e la cosa non si nota finché si prova solo col telecomando — che era
        esattamente come l'avevo provata io.
      */
      onClick={() => onPick?.(channelIndex, 0, false)}
      style={{
        flex: 1,
        minWidth: 0,
        height: ROW_HEIGHT_PX,
        border: 0,
        margin: 0,
        // `0 14px 0 0` come nel prototipo: il riquadro del logo tocca il bordo sinistro, e il respiro
        // sta a destra, fra il nome e la stella.
        padding: '0 14px 0 0',
        borderRadius: 12,
        background: 'transparent',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        color: colors.text,
      }}
    >
      {/* Il logo vero quando c'è; quando manca, il **nome** sul colore del canale, come nel disegno. */}
      <ChannelLogo
        name={displayName}
        logoUrl={logoUrl}
        widthPx={layout.guideLogoWidth}
        heightPx={layout.guideLogoHeight}
        nameFontPx={17}
        colorSeed={channelId}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ font: `700 23px ${FONT_STACK}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          {/*
            Solo il numero: la stella era qui come **contrassegno**, e adesso è un tasto a destra.
            Averne due che dicono la stessa cosa fa cercare la differenza che non c'è.
          */}
          {number}
        </span>
        <span
          style={{
            font: `500 18px ${FONT_STACK}`,
            color: colors.textDim,
            /*
              Due righe, poi i puntini — come nella colonna delle categorie.

              Il nome del canale è **l'informazione essenziale** di una guida: con una riga sola e
              132 px di spazio si leggevano solo le sigle, e «Sky Sport Uno H…» non si riconosce. La
              riga è alta 88 e il logo 66: due righe da 18 ci stanno comodamente sotto il numero.
            */
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.15,
            wordBreak: 'break-word',
            maxWidth: layout.guideChannelWidth - layout.guideLogoWidth - 24,
          }}
        >
          {displayName}
        </span>
      </div>
    </button>

    {/*
      La stella: **sempre visibile**, piena quando è preferito e vuota quando non lo è.

      È il tasto che sostituisce la didascalia. Mostrarla solo sui preferiti — com'era — la rende un
      contrassegno e non un comando: si capisce cosa significa, non che si può premere.
    */}
    {onToggleFavorite !== undefined && (
      <button
        type="button"
        data-f="1"
        data-favorite-toggle={favorite ? '1' : '0'}
        aria-pressed={favorite}
        onClick={onToggleFavorite}
        style={{
          flex: 'none',
          width: 44,
          height: 44,
          border: 0,
          margin: 0,
          padding: 0,
          borderRadius: 22,
          background: 'transparent',
          cursor: 'pointer',
          color: favorite ? colors.accent : colors.textFaint,
          font: `400 26px ${FONT_STACK}`,
          lineHeight: 1,
        }}
      >
        {favorite ? '★' : '☆'}
      </button>
    )}
    </div>
  );
}

function ProgramCells({
  channelIndex,
  topPx,
  programs,
  window: guideWindow,
  serverNowMs,
  reminderIds,
  onPick,
}: {
  channelIndex: number;
  topPx: number;
  programs: ProgramStore;
  window: GuideWindow;
  serverNowMs: number;
  reminderIds: ReadonlySet<string>;
  onPick?: (channelIndex: number, slot: number, confirmed: boolean) => void;
}): ReactNode {
  const translator = useTranslator();
  const [from, to] = programs.slotsInWindow(channelIndex, guideWindow);
  const cells: ReactNode[] = [];
  for (let slot = from; slot < to; slot += 1) {
    const program = programs.materialize(channelIndex, slot);
    const placement = layoutProgram(program, guideWindow);
    if (placement.widthPx <= 0) continue;
    const state = programLiveness(program, serverNowMs);
    const percent = programProgressPercent(program, serverNowMs);
    const reminded = hasReminder(reminderIds, program.channelId, program.startMs);
    cells.push(
      <button
        key={slot}
        type="button"
        data-f="1"
        data-program-cell={`${channelIndex}:${slot}`}
        // Il clic su un programma è già una scelta: si sa cosa si è premuto, a differenza della
        // colonna del canale dove il clic dice solo «questa riga».
        onClick={() => onPick?.(channelIndex, slot, true)}
        style={{
          position: 'absolute',
          top: 0,
          border: 0,
          margin: 0,
          textAlign: 'left',
          cursor: 'pointer',
          left: placement.leftPx,
          width: placement.widthPx,
          height: ROW_HEIGHT_PX,
          background: state === 'live' ? colors.surfaceProgramLive : colors.surfaceProgram,
          color: colors.text,
          borderRadius: 10,
          padding: `0 ${PROGRAM_GAP_PX * 3}px`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 4,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            font: `600 23px ${FONT_STACK}`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {placement.clippedAtStart ? '‹ ' : ''}
          {program.title}
        </span>
        <span
          style={{
            font: `500 17px ${FONT_STACK}`,
            color: colors.textDim,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {/*
            Orario e genere, **e il separatore solo se c'è il secondo**.

            Era `{ora} · {genere}` scritto in JSX: con il genere vuoto — cioè su tutta la guida, perché
            `get_simple_data_table` non lo manda — restava un «17:00 ·» con il puntino appeso nel
            vuoto. Un separatore senza niente dopo non è un dettaglio tipografico: sembra un dato che
            non è arrivato.
          */}
          {/*
            Con un promemoria messo, la riga **lo dice** al posto dell'orario e del genere.

            È l'unico segno che quel programma è già stato scelto: senza, l'unico modo di saperlo era
            premerci sopra e vedere se lo toglieva.
          */}
          {reminded
            ? `● ${translator.t('guide.reminderAt', { time: translator.time(program.startMs) })}`
            : [translator.time(program.startMs), program.genre]
                .filter((part) => part !== '')
                .join(' · ')}
        </span>
        {percent > 0 ? (
          <div
            style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 4, background: colors.border }}
          >
            <div style={{ height: '100%', width: `${percent}%`, background: colors.accent }} />
          </div>
        ) : null}
      </button>,
    );
  }
  return (
    <div
      data-program-row={channelIndex}
      style={{
        position: 'absolute',
        top: topPx,
        left: 0,
        right: 0,
        height: ROW_HEIGHT_PX,
        contain: 'layout paint',
        // Sopra il riquadro del fuoco: vedi il commento là, il fondo è opaco e copriva i dati.
        zIndex: 1,
      }}
    >
      {cells}
    </div>
  );
}
